/**
 * changeOrderEngine.js — Change Order pricing + delta logic (§3.3)
 *
 * CO line items carry the SAME pricing shape as quote line items so delta
 * math, billing, and invoicing work (§0.3). Reuses marginEngine for all
 * money math. All monetary values are in cents.
 *
 * A Change Order is modeled as a working copy of the original quote's line
 * items. Each CO line tracks its origin and a status:
 *   - 'unchanged'  : identical to the original quote line
 *   - 'changed'    : qty/cost/margin edited (revised differs from original)
 *   - 'added'      : new line added in this CO (no original)
 *   - 'cancelled'  : line credited/removed (revised total = 0, credit = original)
 *   - 'locked'     : belongs to a completed WO — no edits allowed (§2.3)
 */

import { computeLineItem, alternatePriceDiff } from '../quote-builder/marginEngine';
import { distributeToTarget, targetEditability } from '../quote-builder/distributeToTarget';
import { rederiveCategoryMargin } from '../quote-builder/seedCatalog';

/**
 * IDI-190/IDI-53: normalize a job/quote line's material+labor cost to cents,
 * across the three known schemas:
 *   1. Quote-sourced camelCase: materialCost / laborCost
 *   2. Job snake_case: cost_basis (per-unit base) / labor implied in cost_basis
 *   3. Job/WO-embedded (idi20_embed_from_line_item, verified live on job
 *      43782): materialCostCents + totalLaborCostCents, whose SUM equals
 *      lineTotal (53465 + 6600 = 60065). Do NOT use laborCostCents
 *      (singular) here — that is a PER-UNIT rate (laborCostCents=5 ×
 *      quantity=1320 = totalLaborCostCents=6600), not a total; using it
 *      directly would silently understate labor cost by ~99%.
 * Mirrors cmd/invoices' getLineItemMoney (Go) / InvoiceReviewSend.
 * normalizeLineItem (IDI-53), which hit the identical schema problem.
 */
export function getLineCost(line) {
  const materialCost = line.materialCost ?? line.material_cost ?? line.cost_basis ?? line.materialCostCents ?? 0;
  const laborCost = line.laborCost ?? line.labor_cost ?? line.totalLaborCostCents ?? 0;
  const otherCost = line.otherCost ?? 0;
  return { materialCost, laborCost, otherCost, totalCost: materialCost + laborCost + otherCost };
}

/**
 * Build a CO working line from an original quote line.
 * The `original` snapshot is frozen so deltas survive edits.
 *
 * @param {object} quoteLine
 * @param {object} [opts]
 * @param {boolean} [opts.locked=false]
 * @param {number|object} [opts.marginContext] - PT-30 margin context. Either a bare
 *   number (legacy — treated as `quoteMargin`) or an object with
 *   `{ customerMargin, customerTypeMargin, quoteMargin }`. Defaults to
 *   `quoteLine.overallMargin` for backward compatibility with pre-PT-30 CO seeding.
 */
export function seedChangeOrderLine(quoteLine, opts = {}) {
  // `opts` may be an Array#map index when used as `lines.map(seedChangeOrderLine)`.
  const o = opts && typeof opts === 'object' ? opts : {};
  const { locked = false, marginContext } = o;
  // DEF-161: job line items store snake_case keys (item_name, cost_basis,
  // unit_rate, line_total, margin_pct). Quotes store camelCase. The seed
  // must read BOTH spellings so it works regardless of source.
  const description = quoteLine.description || quoteLine.item_name || '';
  const productLabel = quoteLine.productLabel || quoteLine.product_label || quoteLine.item_name || description;
  const areaId = quoteLine.areaId || quoteLine.area_id || null;
  const areaLabel = quoteLine.areaLabel || quoteLine.area_label || null;
  const categoryLabel = quoteLine.categoryLabel || quoteLine.category_label || null;
  const unit = quoteLine.unit || null;
  const sqft = quoteLine.sqft ?? quoteLine.sq_ft ?? null;
  const quantity = Number(quoteLine.quantity) || 1;
  // Cost fields: see getLineCost() above for the 3-schema normalization
  // (IDI-190). BP-5: `??` not `||` on money — a legitimate 0 must not fall
  // through to a stale alias (the 601-divergent-alias tail in DEF-149).
  const { materialCost, laborCost, otherCost } = getLineCost(quoteLine);
  const lineTotal = quoteLine.lineTotal ?? quoteLine.line_total ?? null;
  const lineMargin = quoteLine.lineMargin ?? (quoteLine.margin_pct != null ? quoteLine.margin_pct / 100 : null);
  const unitPrice = quoteLine.unitPrice ?? quoteLine.unit_price ?? quoteLine.unit_rate ?? null;

  // IDI-534: never copy a stored categoryMargin into a CO: lines saved
  // before #596 carry the old seed (35% / 50%) the dealer never set. The
  // caller passes the dealer's Settings category_margins; the margin is
  // re-derived from those (null when the dealer has none).
  const categoryMargin = rederiveCategoryMargin(
    { categoryId: quoteLine.categoryId ?? quoteLine.category, categoryMargin: null },
    o.tenantCategoryMargins || null,
  ).categoryMargin ?? null;

  // Build a normalized line for computeLineItem
  const normalized = {
    materialCost,
    laborCost,
    otherCost,
    lineTotal,
    lineMargin,
    categoryMargin,
    isCustom: !!quoteLine.isCustom,
  };
  const ctx = marginContext !== undefined ? marginContext : (quoteLine.overallMargin || 0);
  const computed = computeLineItem(normalized, ctx);

  return {
    id: quoteLine.id,
    quoteLineId: quoteLine.id,
    description,
    productLabel,
    areaId,
    areaLabel,
    categoryLabel,
    isCustom: !!quoteLine.isCustom,
    // Preserve unit fields for display
    unit,
    sqft,
    unitPrice,
    // editable working values (normalized to numbers)
    quantity,
    materialCost,
    laborCost,
    otherCost,
    lineMargin,
    categoryMargin,
    lineTotal,
    // frozen original snapshot for delta math
    original: {
      total: computed.total,
      cost: computed.cost,
      quantity,
    },
    status: locked ? 'locked' : 'unchanged',
    locked,
  };
}

/**
 * Compute the per-line delta: original vs revised vs net. Credits are negative.
 *
 * @param {object} coLine - CO working line
 * @param {number|object} [marginContext=0] - PT-30 margin context (see marginEngine.js).
 *   Bare number is treated as `quoteMargin` (backward compat with pre-PT-30 callers).
 */
export function computeLineDelta(coLine, marginContext = 0) {
  // IDI-543: a CO line's own fields (materialCost/laborCost/otherCost,
  // lineTotal) are ALWAYS at its current quantity. The inline quantity edit
  // (handleUpdateQuantity -> rescaleLineQuantity) and the Edit wizard put
  // them there; that is the single place a quantity change is applied. This
  // engine used to multiply total and cost by new/original quantity again,
  // double-scaling every quantity-edited line, and because the serializer
  // writes revisedTotal back as line_total the error compounded on every
  // reopen. Revised price and revised cost now follow the same rule IDI-534
  // set for target-priced lines: stored at the new quantity, never re-scaled.
  const computed = computeLineItem(coLine, marginContext);
  const revisedTotal = coLine.status === 'cancelled' ? 0 : Math.round(computed.total);
  const originalTotal = coLine.original ? coLine.original.total : 0;
  const net = alternatePriceDiff(originalTotal, revisedTotal); // revised - original
  // IDI-190: cost-side delta, same cancelled treatment as the revenue side
  // above (and, per IDI-543, no re-scaling by quantity), so the Cost Breakdown panel can be driven by
  // costDelta (summed across lines) rather than raw per-line cost. This is
  // what makes the panel read $0.00 on a freshly-opened CO (every line is
  // 'unchanged' → revisedCost === originalCost → costDelta === 0) while
  // still updating the instant a real change (qty edit, item swap, added
  // line) is made — the stakeholder-ruled behavior (see IDI-190 comment).
  const revisedCost = coLine.status === 'cancelled' ? 0 : Math.round(computed.cost);
  const originalCost = coLine.original ? (coLine.original.cost || 0) : 0;
  const costDelta = revisedCost - originalCost;
  return {
    ...coLine,
    revisedTotal,
    originalTotal,
    net, // positive = additional charge, negative = credit
    isCredit: net < 0,
    revisedCost,
    originalCost,
    costDelta,
    // IDI-190: AreaCard/CommittedLineCard (reused verbatim from the quote
    // builder per PT-45's "REUSE, not fork") read computed.total/.cost/
    // .margin/.marginSource -- the shape useQuoteMargins produces for a
    // QUOTE line. This engine never surfaced that shape on its own
    // per-line output (only revisedTotal/revisedCost, used internally
    // above), so every CO line rendered $0.00 regardless of real cost --
    // confirmed live on CO-000107 (job 17248): all 3 unchanged lines with
    // real lineTotal values (13142/157283/7184 cents) rendered $0.00.
    // Alias to the REVISED (current) values, matching the per-line ruling
    // in this ticket's comment thread (show current cost, not frozen
    // original, on each line row -- the breakdown panel is the one place
    // that intentionally shows delta/$0 on an unchanged CO).
    total: revisedTotal,
    cost: revisedCost,
    margin: computed.margin,
    marginSource: computed.marginSource,
  };
}

/**
 * Roll the whole CO into original / revised / net totals + line deltas.
 *
 * @param {Array} coLines
 * @param {number|object} [marginContext=0] - PT-30 margin context (bare number
 *   for legacy callers, or full `{ customerMargin, customerTypeMargin, quoteMargin }`).
 */
export function computeChangeOrder(coLines = [], marginContext = 0) {
  const lines = coLines.map((l) => computeLineDelta(l, marginContext));
  const originalTotal = lines.reduce((s, l) => s + l.originalTotal, 0);
  const revisedTotal = lines.reduce((s, l) => s + l.revisedTotal, 0);
  const netTotal = revisedTotal - originalTotal;
  return { lines, originalTotal, revisedTotal, netTotal, isCredit: netTotal < 0 };
}

/** Mark a line changed/cancelled/added; no-op on locked lines (§2.3). */
export function setLineStatus(coLine, status) {
  if (coLine.locked) return coLine;
  return { ...coLine, status };
}

/**
 * IDI-191: "Change Item" replaces a CO line's product/pricing with a
 * different item picked from the Add-Item wizard, as opposed to "Edit"
 * which only tweaks sqft/margin/qty on the SAME product. Used when a line
 * needs to be swapped out entirely (e.g. wrong product was quoted).
 *
 * - An original (quoted/migrated) line keeps its id and frozen `original`
 *   snapshot so delta math (computeLineDelta/applyChangeOrderToQuote) and
 *   Undo still work against the pre-change baseline, and is marked
 *   'changed' -- exactly the same transition an EditItemModal edit produces.
 * - A line that was itself 'added' this session has no original to
 *   preserve; it is simply replaced outright and stays 'added'.
 * - Locked lines are never replaced (§2.3).
 *
 * @param {object} targetLine - the existing CO line being replaced
 * @param {object} newItem - the new item payload from AddItemCascade.onAddItem
 */
export function replaceChangeOrderLine(targetLine, newItem) {
  if (targetLine.locked) return targetLine;
  const replaced = {
    ...newItem,
    id: targetLine.id,
    locked: targetLine.locked,
    original: targetLine.original,
  };
  if (targetLine.original && targetLine.status !== 'added') {
    return setLineStatus(replaced, 'changed');
  }
  return { ...replaced, status: targetLine.status };
}

/** Per-item Undo Change (§2.5): restore working values to the frozen original. */
export function undoLineChange(coLine) {
  if (coLine.locked || !coLine.original) return coLine;
  return {
    ...coLine,
    quantity: coLine.original.quantity,
    status: 'unchanged',
  };
}

/**
 * On approval (§2.8): produce the mutated quote line set — append added lines,
 * apply revised values to changed lines, drop cancelled lines. Returns a NEW
 * array; never forks into a parallel document.
 */
export function applyChangeOrderToQuote(quoteLines = [], coLines = []) {
  const byId = new Map(quoteLines.map((q) => [q.id, q]));
  coLines.forEach((co) => {
    if (co.status === 'cancelled') {
      byId.delete(co.quoteLineId);
    } else if (co.status === 'added') {
      byId.set(co.id, { ...co });
    } else if (co.status === 'changed') {
      const existing = byId.get(co.quoteLineId) || {};
      byId.set(co.quoteLineId, {
        ...existing,
        quantity: co.quantity,
        materialCost: co.materialCost,
        laborCost: co.laborCost,
        otherCost: co.otherCost,
        lineMargin: co.lineMargin,
        lineTotal: co.lineTotal,
      });
    }
  });
  return Array.from(byId.values());
}


// ─── IDI-309 / F-50: estimate-card target on a change order ──────────────────

/** Only lines this change order adds or changes can be re-priced. */
function isCoTargetEligible(l) {
  return !!l && !l.locked && (l.status === 'added' || l.status === 'changed');
}

const CO_TARGET_OPTS = {
  // Every CO line counts toward the revised total (cancelled lines count 0).
  isIncluded: () => true,
  isEligible: isCoTargetEligible,
  noEligibleMessage: 'Only lines this change order adds or changes can be re-priced. Add or change a line first.',
};

export function coTargetEditability(coLines) {
  return targetEditability(coLines, CO_TARGET_OPTS);
}

/**
 * Re-price a change order's added/changed lines so the NET CHANGE
 * (revised - original, the card's headline) lands on `netTargetCents`.
 * Unchanged, locked and cancelled lines, category-margin lines and Options
 * are fixed. Writes the new price onto each working line's `lineTotal`
 * as-is (it is already at the line's current quantity; IDI-543), persisted
 * via `lines`. Each line is measured at its true revised cost, so the
 * reachable range and the credit floor (IDI-534 Q2) are right.
 */
export function distributeChangeOrderToTarget(coLines, netTargetCents, marginContext = 0) {
  const co = computeChangeOrder(coLines, marginContext);
  const result = distributeToTarget(coLines, netTargetCents, {
    ...CO_TARGET_OPTS,
    measure: (_l, i) => {
      const c = co.lines[i] || {};
      return { total: c.revisedTotal || 0, cost: c.revisedCost || 0 };
    },
    offsetCents: -(co.originalTotal || 0),
    // IDI-534: store the revised (new-quantity) total as-is. computeLineDelta
    // never re-scales a line by quantity (IDI-543), so this number
    // is what the CO bills, what the serializer writes as line_total, and
    // what a reload reads back: no drift.
    writeTotal: (line, total) => {
      const { line_total: _lt, ...rest } = line;
      return { ...rest, lineTotal: total, marginSource: 'price_override' };
    },
  });
  if (!result.ok || result.unchanged) return result;
  // Land the exact figure (the engine's own rounding can leave a cent). This
  // runs whatever the re-priced lines' quantities: it used to skip every line
  // whose quantity changed, so a CO where all of them had changed was left a
  // cent off.
  const after = computeChangeOrder(result.lines, marginContext);
  const diff = Math.round(netTargetCents) - after.netTotal;
  if (diff !== 0) {
    let best = -1;
    result.changes.forEach(({ index }) => {
      if (best < 0 || (result.lines[index].lineTotal ?? 0) > (result.lines[best].lineTotal ?? 0)) best = index;
    });
    if (best >= 0) {
      result.lines = result.lines.map((l, i) => (i === best ? { ...l, lineTotal: l.lineTotal + diff } : l));
    }
  }
  return result;
}
